import { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';

interface Props {
  value: string;
  onChange: (v: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
}

export function OtpInput({ value, onChange, length = 4, disabled, autoFocus = true }: Props) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const setDigit = (i: number, digit: string) => {
    const sanitized = digit.replace(/\D/g, '').slice(-1);
    const next = (value.padEnd(length, ' ').split('').map((c, idx) => (idx === i ? sanitized || ' ' : c)).join('')).trimEnd();
    onChange(next);
    if (sanitized && i < length - 1) refs.current[i + 1]?.focus();
  };

  const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pasted) {
      e.preventDefault();
      onChange(pasted);
      const focusAt = Math.min(pasted.length, length - 1);
      refs.current[focusAt]?.focus();
    }
  };

  return (
    <div
      className="grid w-full gap-2 sm:gap-2.5"
      style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}
      onPaste={handlePaste}
    >
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={value[i] ?? ''}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          className={cn(
            'aspect-[3/4] w-full min-w-0 rounded-lg border border-app-border bg-app-field text-center text-xl font-bold text-app-text transition-colors sm:text-2xl',
            'focus:outline-none focus-visible:border-primary focus-visible:bg-app-surface',
            value[i] && 'border-app-text/30',
            disabled && 'opacity-50',
          )}
          aria-label={`OTP digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
